import { useState, useEffect } from 'react'
import { useCustomValuesStore } from '../../stores/customValuesStore'
import type { CustomValue } from '@creanizer/utils'

interface CustomValuesModalProps {
  isOpen: boolean
  onClose: () => void
}

export function CustomValuesModal({ isOpen, onClose }: CustomValuesModalProps) {
  const {
    customValues,
    deleteCustomValue,
    clearCustomValues,
    exportCustomValues,
    importCustomValues,
  } = useCustomValuesStore()

  const [selectedType, setSelectedType] = useState<'all' | CustomValue['type']>('all')
  const [selectedGenerator, setSelectedGenerator] = useState<'all' | 'name' | 'title' | 'plot' | 'scene' | 'location'>('all')
  const [importData, setImportData] = useState('')
  const [showImport, setShowImport] = useState(false)
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)

  const filteredValues = customValues.filter(v => {
    const typeMatch = selectedType === 'all' || v.type === selectedType
    const generatorMatch = selectedGenerator === 'all' || v.generatorType === selectedGenerator
    return typeMatch && generatorMatch
  })

  const handleExport = async () => {
    try {
      const data = await exportCustomValues()
      const blob = new Blob([data], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `creanizer-custom-values-${new Date().toISOString().split('T')[0]}.json`
      a.click()
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Export failed:', error)
      alert('エクスポートに失敗しました')
    }
  }

  const handleImport = async () => {
    try {
      await importCustomValues(importData)
      setImportData('')
      setShowImport(false)
      alert('インポートが完了しました')
    } catch (error) {
      console.error('Import failed:', error)
      alert('インポートに失敗しました。データ形式を確認してください。')
    }
  }

  const handleDelete = async (id: string) => {
    if (deleteConfirmId === id) {
      try {
        await deleteCustomValue(id)
        setDeleteConfirmId(null)
      } catch (error) {
        console.error('Delete failed:', error)
        alert('削除に失敗しました')
      }
    } else {
      setDeleteConfirmId(id)
      setTimeout(() => setDeleteConfirmId(null), 3000)
    }
  }

  const handleClearAll = async () => {
    if (window.confirm('すべてのカスタム値を削除しますか？この操作は取り消せません。')) {
      try {
        await clearCustomValues()
        alert('すべてのカスタム値を削除しました')
      } catch (error) {
        console.error('Clear all failed:', error)
        alert('削除に失敗しました')
      }
    }
  }

  const getTypeLabel = (type: CustomValue['type']) => {
    switch (type) {
      case 'style': return 'スタイル'
      case 'motif': return 'モチーフ'
      case 'subcategory': return 'サブカテゴリ'
      case 'sceneType': return 'シーンタイプ'
      case 'genre': return 'ジャンル'
      case 'atmosphere': return '雰囲気'
      case 'locationType': return 'ロケーションタイプ'
      case 'purpose': return '用途・目的'
      default: return type
    }
  }

  const getGeneratorLabel = (generatorType?: string) => {
    switch (generatorType) {
      case 'name': return 'ネーム'
      case 'title': return 'タイトル'
      case 'plot': return 'プロット'
      case 'scene': return 'シーン'
      case 'location': return 'ロケーション'
      default: return '共通'
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[80vh] overflow-hidden">
        <div className="p-6 border-b">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold">カスタム値管理</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* フィルタとアクション */}
          <div className="mb-6">
            {/* ジェネレータフィルタ */}
            <div className="flex gap-2 mb-3">
              <span className="text-sm font-medium py-2">ジェネレータ:</span>
              <button
                className={`px-3 py-1 rounded text-sm ${selectedGenerator === 'all' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                onClick={() => setSelectedGenerator('all')}
              >
                すべて
              </button>
              <button
                className={`px-3 py-1 rounded text-sm ${selectedGenerator === 'name' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                onClick={() => setSelectedGenerator('name')}
              >
                ネーム
              </button>
              <button
                className={`px-3 py-1 rounded text-sm ${selectedGenerator === 'title' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                onClick={() => setSelectedGenerator('title')}
              >
                タイトル
              </button>
              <button
                className={`px-3 py-1 rounded text-sm ${selectedGenerator === 'plot' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                onClick={() => setSelectedGenerator('plot')}
              >
                プロット
              </button>
              <button
                className={`px-3 py-1 rounded text-sm ${selectedGenerator === 'scene' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                onClick={() => setSelectedGenerator('scene')}
              >
                シーン
              </button>
              <button
                className={`px-3 py-1 rounded text-sm ${selectedGenerator === 'location' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                onClick={() => setSelectedGenerator('location')}
              >
                ロケーション
              </button>
            </div>
            
            {/* タイプフィルタとアクション */}
            <div className="flex justify-between items-center">
              <div className="flex gap-2">
                <span className="text-sm font-medium py-2">タイプ:</span>
                <button
                  className={`px-3 py-1 rounded text-sm ${selectedType === 'all' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                  onClick={() => setSelectedType('all')}
                >
                  すべて ({filteredValues.length})
                </button>
                <button
                  className={`px-3 py-1 rounded text-sm ${selectedType === 'style' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                  onClick={() => setSelectedType('style')}
                >
                  スタイル
                </button>
                <button
                  className={`px-3 py-1 rounded text-sm ${selectedType === 'motif' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                  onClick={() => setSelectedType('motif')}
                >
                  モチーフ
                </button>
                <button
                  className={`px-3 py-1 rounded text-sm ${selectedType === 'subcategory' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                  onClick={() => setSelectedType('subcategory')}
                >
                  サブカテゴリ
                </button>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={handleExport}
                  className="btn btn-secondary"
                >
                  エクスポート
                </button>
                <button
                  onClick={() => setShowImport(!showImport)}
                  className="btn btn-secondary"
                >
                  インポート
                </button>
                {filteredValues.length > 0 && (
                  <button
                    onClick={handleClearAll}
                    className="btn bg-red-500 text-white hover:bg-red-600"
                  >
                    すべて削除
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* インポートエリア */}
          {showImport && (
            <div className="mb-6 p-4 bg-gray-50 rounded">
              <h3 className="font-semibold mb-2">JSONデータをインポート</h3>
              <textarea
                className="w-full h-32 p-2 border rounded"
                placeholder="エクスポートしたJSONデータを貼り付けてください"
                value={importData}
                onChange={(e) => setImportData(e.target.value)}
              />
              <div className="mt-2 flex gap-2">
                <button
                  onClick={handleImport}
                  className="btn btn-primary"
                  disabled={!importData}
                >
                  インポート実行
                </button>
                <button
                  onClick={() => {
                    setShowImport(false)
                    setImportData('')
                  }}
                  className="btn btn-secondary"
                >
                  キャンセル
                </button>
              </div>
            </div>
          )}

          {/* カスタム値リスト */}
          <div className="overflow-y-auto max-h-[400px]">
            {filteredValues.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                カスタム値がありません
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left">ジェネレータ</th>
                    <th className="px-4 py-2 text-left">タイプ</th>
                    <th className="px-4 py-2 text-left">ラベル</th>
                    <th className="px-4 py-2 text-left">値</th>
                    <th className="px-4 py-2 text-left">名前タイプ</th>
                    <th className="px-4 py-2 text-left">使用回数</th>
                    <th className="px-4 py-2 text-left">作成日</th>
                    <th className="px-4 py-2 text-left">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredValues.map((value) => (
                    <tr key={value.id} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-2">
                        <span className={`px-2 py-1 rounded text-sm font-medium ${
                          (value.generatorType as string) === 'name' ? 'bg-blue-100 text-blue-800' :
                          (value.generatorType as string) === 'title' ? 'bg-green-100 text-green-800' :
                          (value.generatorType as string) === 'plot' ? 'bg-purple-100 text-purple-800' :
                          (value.generatorType as string) === 'character' ? 'bg-orange-100 text-orange-800' :
                          (value.generatorType as string) === 'scene' ? 'bg-indigo-100 text-indigo-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {getGeneratorLabel(value.generatorType)}
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        <span className="px-2 py-1 bg-gray-200 rounded text-sm">
                          {getTypeLabel(value.type)}
                        </span>
                      </td>
                      <td className="px-4 py-2 font-medium">{value.label}</td>
                      <td className="px-4 py-2 text-gray-600" title={value.value}>
                        {value.type === 'writingStyle' && value.value.length > 100
                          ? `${value.value.substring(0, 100)}...`
                          : value.value}
                      </td>
                      <td className="px-4 py-2 text-gray-600">
                        {value.nameType || '-'}
                      </td>
                      <td className="px-4 py-2">{value.usageCount}</td>
                      <td className="px-4 py-2 text-sm text-gray-500">
                        {new Date(value.createdAt).toLocaleDateString('ja-JP')}
                      </td>
                      <td className="px-4 py-2">
                        <button
                          onClick={() => handleDelete(value.id)}
                          className={`px-3 py-1 rounded text-sm ${
                            deleteConfirmId === value.id
                              ? 'bg-red-500 text-white'
                              : 'bg-gray-200 hover:bg-gray-300'
                          }`}
                        >
                          {deleteConfirmId === value.id ? '確認' : '削除'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}